import { useCallback, useEffect, useState } from "react";
import {
	getPermissionData,
	getUserData,
} from "../../Actions/common";
import {
	deleteSingleMultipleBusinessAction,
	getAllWhiteListIpsAction,
	getWhitelistIpByIdAction,
} from "../../Actions/whiteListpsAction";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditWhiteListIp from "./AddEditWhiteListIp";
import showConfirmation from "../../utils/Conformation";
import DataTable from "../../components/DataTable/DataTable";

const Whitelist = ({ merchantId }) => {
	const [totalRecords, setTotalRecords] = useState(0);
	const [loading, setLoading] = useState(true);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [shouldFetchData, setShouldFetchData] = useState(1);
	const [ipDataById, setIpDataById] = useState(null);
	const [whiteListIpsData, setWhiteListIpsData] = useState([]);
	const [anchorEl, setAnchorEl] = useState(null);
	const isMenuOpen = Boolean(anchorEl);
	const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(
		ITEMS_PER_PAGE_OPTIONS[0]
	);

	const [openModal, setOpenModal] = useState(false);

	const userData = localStorage.getItem("userData") ? getUserData() : null;
	const permissionData = localStorage.getItem(
		`${userData?.id}_permissionData`
	)
		? getPermissionData(`${userData?.id}_permissionData`)
		: [];
	const hasPermission = permissionData[0]?.role_permissions?.find(
		(m) => m?.module && m?.module?.slug === "ip-whitelist"
	);

	const [filterValues, setFilterValues] = useState({
		search: "",
	});

	const getWhiteListIps = useCallback(async () => {
		setLoading(true);
		await getAllWhiteListIpsAction(
			currentPage,
			filterValues.search,
			selectedRowsPerPage,
			null,
			null,
			merchantId ?? undefined
		)
			.then((res) => {
				const { rows, count } = res.data.data;
				if (res?.data.status === 202) {
					setSnackbar({
						open: true,
						message: res?.data?.message,
						severity: "info",
					});
					return;
				}
				setWhiteListIpsData(rows);
				setTotalRecords(count);
				setLoading(false);
			})
			.catch((error) => {
				setSnackbar({
					open: true,
					message: error.response.data.message,
					severity: "error",
				});
			});
		setLoading(false);
	}, [currentPage, filterValues.search, selectedRowsPerPage, merchantId]);

	useEffect(() => {
		if (shouldFetchData) {
			getWhiteListIps();
			setShouldFetchData(0);
		}
	}, [shouldFetchData, getWhiteListIps]);

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const handleDelete = async (e, row) => {
		e.stopPropagation();

		await showConfirmation("simpleDelete", {
			entityName: "Merchant",
			entityId: row,
			action: deleteSingleMultipleBusinessAction,
			onSuccess: (res) => {
				getWhiteListIps();
			},
			onError: (error) => {
				setSnackbar({
					open: true,
					message: error?.response?.data?.message || "Something went wrong",
					severity: "error",
				});
			},
			showCancelledMsg: true,
		});
	};

	const handleAddIp = async (e, row) => {
		if (row) {
			await getWhitelistIpByIdAction(row)
				.then((res) => {
					setIpDataById(res.data);
					setOpenModal(true);
				})
				.catch((error) => {
					setSnackbar({
						open: true,
						message: error?.response?.data?.message,
						severity: "error",
					});
				});
		} else {
			setOpenModal(true);
		}
	};

	const handleCloseModal = () => {
		setOpenModal(false);
		setIpDataById(null);
	};

	const columns = [
		{
			field: "ipAddress",
			headerName: "IP Address",
			minWidth: 150,
		},
	];

	const filters = [
		{
			type: "search",
			key: "search",
			placeholder: "Search IP Address",
		},
	];

	const handleFilterApply = (values) => {
		setFilterValues(values);
		setCurrentPage(1);
		setShouldFetchData(1);
	};

	const handleFilterReset = () => {
		setFilterValues({
			search: "",
		});

		setCurrentPage(1);
		setShouldFetchData(1);
	};

	const actions = {
		edit: hasPermission?.canEdit,
		delete: hasPermission?.canDelete,
	};

	const handleTableAction = async (action, row) => {
		switch (action) {
			case "edit":
				handleAddIp(null, row.id);
				break;

			case "delete":
				handleDelete(
					{ stopPropagation: () => { } },
					row.id
				);
				break;

			default:
				break;
		}
	};

	const bulkActions = {
		delete: hasPermission?.canDelete,
	};

	const handleBulkAction = async (action, ids) => {
		if (action !== "delete") return;

		const result = await showConfirmation("simpleMultipleDelete", {
			entityName: "Merchant",
			entityIds: ids,
			action: async (ids) => {
				return await deleteSingleMultipleBusinessAction({ ids });
			},
			onSuccess: () => {
				getWhiteListIps();
			},
			onError: (error) => {
				setSnackbar({
					open: true,
					message:
						error?.response?.data?.message ||
						"Something went wrong",
					severity: "error",
				});
			},
		});

		if (result?.cancelled) {
			return;
		}
	};

	return (
		<div id="kt_content_container">
			<BreadcrumbsComponent />

			<DataTable
				columns={columns}
				rows={whiteListIpsData}
				loading={loading}
				totalCount={totalRecords}
				page={currentPage}
				pageSize={selectedRowsPerPage}
				filters={filters}
				filterValues={filterValues}
				actions={actions}
				bulkActions={bulkActions}
				getRowId={(row) => row.id}
				onAction={handleTableAction}
				onBulkAction={handleBulkAction}
				onFilterApply={handleFilterApply}
				onFilterReset={handleFilterReset}
				onPageChange={(page) => {
					setCurrentPage(page);
					setShouldFetchData(1);
				}}
				onPageSizeChange={(size) => {
					setSelectedRowsPerPage(size);
					setCurrentPage(1);
					setShouldFetchData(1);
				}}
				hasCreatePermission={hasPermission?.canCreate}
				addButtonText="ADD"
				onAddClick={handleAddIp}
			/>
			{openModal ? (
				<AddEditWhiteListIp
					getIpData={whiteListIpsData}
					open={openModal}
					handleClose={handleCloseModal}
					ipDataById={ipDataById}
					setSnackbar={setSnackbar}
					getWhiteListIps={getWhiteListIps}
				/>
			) : null}

			<CustomSnackbar
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={handleCloseSnackbar}
			/>
		</div>
	);
};

export default Whitelist;
